/**
 * Priority Achievability — one row per priority showing full achievability status.
 * Corresponds to spec's PRIORITY_ACHIEVABILITY output sheet.
 */

import { useMemo, useState } from 'react';
import { Loader2, Target, Zap } from 'lucide-react';
import { usePlanningEngine } from './PlanningEngineContext';
import { engineInputKindLabel } from '../../../lib/planningEngineService';
import type { PriorityFeasibility, DecisionWithParticipation } from '../../../lib/planningEngineModule.types';
import { PLANNING_MODE_LABELS } from '../../../lib/planningEngineModule.types';

type FeasibilityFilter = 'all' | PriorityFeasibility | 'at_risk';

function feasibilityClass(f: PriorityFeasibility): string {
  switch (f) {
    case 'Fully Achievable': return 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]';
    case 'Partially Achievable': return 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]';
    case 'Not Achievable': return 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]';
    case 'No Work': return 'bg-[var(--surface)] text-[var(--text-ter)] dark:bg-[var(--bg)]/20';
  }
}

function participationClass(d: DecisionWithParticipation): string {
  if (d === 'Included') return 'text-[var(--green)] dark:text-[var(--text-ter)]';
  if (d === 'At Risk - Participation' || d === 'At Risk - Capacity') return 'text-[var(--mustard)] dark:text-[var(--mustard)]';
  if (d === 'Blocked - Capability') return 'text-[var(--coral)] dark:text-[var(--coral)]';
  return 'text-[var(--text-ter)]';
}

export default function PriorityAchievabilityView() {
  const { planOutput, running, error, runEngineForScenario, activeScenario, scopedItemIds, engineInput, runBlockedReason } = usePlanningEngine();
  const [feasibilityFilter, setFeasibilityFilter] = useState<FeasibilityFilter>('all');
  const allSummaries = planOutput?.prioritySummaries ?? [];
  // Apply input-scope filter so this view stays in sync with the selected pool/collection.
  const scopedSummaries = scopedItemIds
    ? allSummaries.filter((s) => scopedItemIds.has(s.priority.id))
    : allSummaries;

  const summaries = useMemo(() => {
    let rows = scopedSummaries;
    if (feasibilityFilter === 'at_risk') {
      rows = rows.filter((s) => s.hasParticipationRisk);
    } else if (feasibilityFilter === 'Fully Achievable') {
      rows = rows.filter(
        (s) => s.feasibility === 'Fully Achievable' || s.feasibility === 'Partially Achievable',
      );
    } else if (feasibilityFilter !== 'all') {
      rows = rows.filter((s) => s.feasibility === feasibilityFilter);
    }
    // Default view by rank — the # column already displays forceRank, but row
    // ORDER was previously raw engine-output order, not sorted by it.
    return [...rows].sort((a, b) => {
      const ar = a.priority.forceRank ?? Number.MAX_SAFE_INTEGER;
      const br = b.priority.forceRank ?? Number.MAX_SAFE_INTEGER;
      return ar - br;
    });
  }, [scopedSummaries, feasibilityFilter]);

  const scopeBanner = scopedItemIds && engineInput.type !== 'live'
    ? `Scoped to ${engineInputKindLabel(engineInput.type)}: ${'name' in engineInput ? engineInput.name : ''} — ${scopedSummaries.length} of ${allSummaries.length} priorities`
    : null;

  const aboveCount = scopedSummaries.filter((s) => s.feasibility === 'Fully Achievable' || s.feasibility === 'Partially Achievable').length;
  const atRiskCount = scopedSummaries.filter((s) => s.hasParticipationRisk).length;
  const belowCount = scopedSummaries.filter((s) => s.feasibility === 'Not Achievable').length;

  return (
    <div className="p-4 md:p-6 w-full h-full overflow-y-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20">
              <Target className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            </div>
            <h2 className="text-xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Priority Achievability
            </h2>
          </div>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] ml-10">
            {planOutput
              ? `${scopedSummaries.length} priorit${scopedSummaries.length !== 1 ? 'ies' : 'y'} · ${PLANNING_MODE_LABELS[planOutput.planningMode]} · ${aboveCount} achievable · ${atRiskCount} at risk`
              : 'One row per priority showing achievability status'}
          </p>
        </div>
        <button
          onClick={() => void runEngineForScenario()}
          disabled={running || runBlockedReason != null}
            title={runBlockedReason ?? undefined}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          Run Engine
        </button>
      </div>

      {engineInput.type === 'live' && planOutput && (
        <div className="bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 border border-[var(--mustard)] dark:border-[var(--mustard)] px-3 py-2 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
          Input scope is <span className="font-medium">All Live Items</span> — every active item in the workspace.
          Use the <span className="font-medium">Input Scope</span> control above to limit this view to a Queue, Item Pool, Collection, or Published List.
        </div>
      )}

      {scopeBanner && (
        <div className="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)] px-3 py-2 text-sm text-[var(--text-sec)]">
          {scopeBanner}
        </div>
      )}

      {error && (
        <div className="bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3 text-sm text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {/* Stat bar — clickable filters */}
      {planOutput && (
        <div className="grid grid-cols-3 gap-4">
          {([
            { key: 'Fully Achievable' as const, label: 'Achievable', count: aboveCount, color: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
            { key: 'at_risk' as const, label: 'At Risk', count: atRiskCount, color: 'text-[var(--mustard)] dark:text-[var(--mustard)]' },
            { key: 'Not Achievable' as const, label: 'Not Achievable', count: belowCount, color: 'text-[var(--coral)] dark:text-[var(--coral)]' },
          ]).map(({ key, label, count, color }) => {
            const active = feasibilityFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFeasibilityFilter(active ? 'all' : key)}
                className={`bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border  p-4 text-center transition-all ${
                  active
                    ? 'border-primary-400 ring-2 ring-primary-200 dark:ring-primary-800'
                    : 'border-[var(--rule)] dark:border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className={`text-3xl font-bold ${color}`}>{count}</div>
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-1">
                  {label}{active ? ' · filtered' : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!planOutput ? (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-12 text-center">
          <Target className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            No results yet. Run the engine to see priority-level achievability.
          </p>
          {activeScenario && (
            <button
              onClick={() => void runEngineForScenario()}
              disabled={running}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-sm font-medium mx-auto"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Run Engine
            </button>
          )}
        </div>
      ) : (
        <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              <colgroup>
                <col className="w-12" />
                <col />
                <col className="w-28" />
                <col className="w-28" />
                <col className="w-28" />
                <col className="w-24" />
                <col className="w-40" />
                <col className="w-44" />
              </colgroup>
              <thead className="bg-[var(--surface)] dark:bg-[var(--bg)]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Resource</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Required WU</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Included WU</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Coverage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Feasibility</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">Participation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {summaries.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                      {feasibilityFilter !== 'all'
                        ? 'No priorities match this filter. Click the active stat card again to clear.'
                        : 'No priorities in this run.'}
                    </td>
                  </tr>
                )}
                {summaries.map((ps, i) => (
                  <tr key={ps.priority.id} className="hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                    <td className="px-4 py-3 text-xs text-[var(--text-ter)] tabular-nums">
                      {ps.priority.forceRank ?? i + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] break-words">
                      {ps.priority.name}
                      {ps.blockedReason && (
                        <p className="mt-0.5 text-xs font-normal text-[var(--coral)]">{ps.blockedReason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--text-ter)] capitalize">
                      {ps.priority.priorityResourceType}
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                      {ps.requiredWU > 0 ? ps.requiredWU.toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-[var(--green)] dark:text-[var(--text-ter)]">
                      {ps.includedWU > 0 ? ps.includedWU.toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                      {ps.requiredWU > 0 ? `${ps.coveragePercent}%` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${feasibilityClass(ps.feasibility)}`}>
                        {ps.feasibility}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {ps.hasParticipationRisk ? (
                        <span className={`text-xs font-medium ${participationClass(ps.decisionWithParticipation)}`}>
                          {ps.decisionWithParticipation}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--text-ter)]">Clear</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
